import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Brain, Clock, BarChart3, Shield, Sparkles } from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/80 backdrop-blur-xl border-b border-slate-100 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Brain className="w-8 h-8 text-indigo-500" />
              <h1 className="font-heading text-2xl font-bold text-slate-900">PsychTest</h1>
            </div>
            <div className="hidden sm:flex items-center gap-1 text-xs text-slate-500 border-l border-slate-200 pl-3">
              <Sparkles className="w-3 h-3" />
              <span>Powered by</span>
              <span className="font-semibold text-indigo-600">Uxima Foundation</span>
            </div>
          </div>
          <div className="flex gap-3">
            <Button
              data-testid="csr-login-nav-btn"
              onClick={() => navigate('/csr/login')}
              variant="ghost"
              className="font-medium text-teal-600 hover:text-teal-700"
            >
              CSR Partner
            </Button>
            <Button
              data-testid="admin-login-nav-btn"
              onClick={() => navigate('/admin/login')}
              variant="ghost"
              className="font-medium"
            >
              Admin Login
            </Button>
            <Button
              data-testid="get-started-nav-btn"
              onClick={() => navigate('/auth')}
              className="rounded-full px-8 py-3 bg-indigo-500 hover:bg-indigo-600 text-white font-semibold transition-all hover:scale-105 shadow-lg shadow-indigo-500/20"
            >
              Get Started
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-24 px-6">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <div className="space-y-6 animate-fadeIn">
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 leading-tight">
              Discover Your
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-indigo-500 to-violet-500">
                True Potential
              </span>
            </h1>
            <p className="text-lg text-slate-600">
              Take our scientifically designed psychometric test and unlock insights about your cognitive strengths, personality traits, and career aptitude.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button
                data-testid="hero-start-test-btn"
                onClick={() => navigate('/auth')}
                className="rounded-full px-8 py-3 bg-indigo-500 hover:bg-indigo-600 text-white font-semibold transition-all hover:scale-105 shadow-lg shadow-indigo-500/20"
              >
                Start Your Test
              </Button>
              <Button
                variant="outline"
                className="rounded-full px-8 py-3 bg-white text-indigo-900 border border-slate-200 hover:bg-slate-50 font-medium transition-all"
              >
                Learn More
              </Button>
            </div>
          </div>
          <div className="relative">
            <img
              src="https://images.unsplash.com/photo-1758521541324-d304c5303fe5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwzfHxoYXBweSUyMHN0dWRlbnQlMjBsYXB0b3AlMjBzdHVkeXxlbnwwfHx8fDE3NzI3NzcyNzV8MA&ixlib=rb-4.1.0&q=85"
              alt="Happy student studying"
              className="rounded-2xl shadow-2xl"
            />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-6 bg-white/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Why Choose Our Assessment?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Our comprehensive testing platform is designed to give you accurate, actionable insights
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: <Brain className="w-8 h-8 text-indigo-500" />,
                title: 'Scientific Approach',
                description: 'Questions designed by psychometric experts'
              },
              {
                icon: <Clock className="w-8 h-8 text-teal-500" />,
                title: 'Timed Assessment',
                description: '30-minute focused test for accurate results'
              },
              {
                icon: <BarChart3 className="w-8 h-8 text-amber-500" />,
                title: 'Detailed Reports',
                description: 'Comprehensive analysis of your performance'
              },
              {
                icon: <Shield className="w-8 h-8 text-purple-500" />,
                title: 'Secure Platform',
                description: 'Your data is protected and confidential'
              }
            ].map((feature, index) => (
              <div
                key={index}
                data-testid={`feature-card-${index}`}
                className="group bg-white rounded-2xl border border-slate-100 p-6 hover:border-indigo-200 hover:shadow-lg transition-all cursor-pointer hover:-translate-y-1"
              >
                <div className="mb-4">{feature.icon}</div>
                <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-slate-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-r from-indigo-500 to-violet-500 rounded-3xl p-12 shadow-2xl">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">
              Ready to Begin Your Journey?
            </h2>
            <p className="text-lg text-indigo-100 mb-8">
              Join thousands of students who have discovered their strengths
            </p>
            <Button
              data-testid="cta-register-btn"
              onClick={() => navigate('/auth')}
              className="rounded-full px-8 py-4 bg-white text-indigo-600 hover:bg-indigo-50 font-semibold transition-all hover:scale-105 shadow-lg text-lg"
            >
              <span className="line-through text-slate-400 mr-2">₹299</span>
              <span className="text-teal-600 font-bold">Register FREE</span>
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex flex-col items-center md:items-start gap-2">
              <div className="flex items-center gap-2">
                <Brain className="w-6 h-6" />
                <span className="font-heading text-xl font-bold">PsychTest</span>
              </div>
              <div className="flex items-center gap-1 text-sm text-slate-400">
                <Sparkles className="w-3 h-3" />
                <span>Powered by</span>
                <span className="font-semibold text-indigo-400">Uxima Foundation</span>
              </div>
            </div>
            <div className="text-center md:text-right">
              <p className="text-slate-400 text-sm">
                Empowering students with data-driven career guidance
              </p>
              <p className="text-slate-500 text-xs mt-1">
                © {new Date().getFullYear()} Uxima Foundation. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;